/**
 * Profile block at the foot of the rail — hybrid-3c §4 "Profile block".
 *
 * A recessed plate (one step deeper than the rail, not a card): avatar, name
 * and role, and a chevron that opens the reader's menu upwards — the session
 * countdown, the language and sign-out. Collapsed, only the avatar remains and
 * the name and role move to a tooltip. The row is a Box, so its keyboard
 * behaviour is wired by hand.
 *
 * It also keeps the SSO session alive: while the reader is active the token is
 * refreshed before it expires, and an expired session goes back to the portal.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useGetIdentity, useLocaleState, useLocales, useLogout, useTranslate } from 'react-admin';
import {
  Avatar,
  Box,
  Chip,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  AccessTimeRounded as TimeIcon,
  CheckRounded as CheckIcon,
  LogoutRounded as LogoutIcon,
  TranslateRounded as TranslateIcon,
  UnfoldMoreRounded as UnfoldMoreIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import sso from '@/lib/sso';
import { usePanelAccess } from '@/features/developers/access';
import { DEVELOPERS_ENTRY, MENU_ENTRIES } from './menuStructure';
import { clearSsoAuthData, syncAuthToLocalStorage } from '@/utils/authSync';

const PORTAL_URL = import.meta.env.VITE_PORTAL_URL || 'https://portal.roommatik.com';

const getInitials = (fullName?: string | null): string => {
  if (!fullName) return '?';
  const parts = fullName.trim().split(/\s+/);
  const initials = parts.length > 1 ? `${parts[0][0]}${parts[parts.length - 1][0]}` : parts[0].slice(0, 2);
  return initials.toUpperCase();
};

interface ProfileRailCardProps {
  /** The rail is open (or is the phone drawer): show the plate, not just the avatar. */
  expanded: boolean;
}

export const ProfileRailCard: React.FC<ProfileRailCardProps> = ({ expanded }) => {
  const theme = useTheme();
  const translate = useTranslate();
  const logout = useLogout();
  const { data: identity, isLoading } = useGetIdentity();
  const [locale, setLocale] = useLocaleState();
  const locales = useLocales();
  const profile = theme.custom.menu.profile;
  const navigate = useNavigate();
  // Anyone who can read the API documentation reaches each of its tabs from here.
  const developerTabs = usePanelAccess() === 'none' ? [] : MENU_ENTRIES.find((e) => e.name === DEVELOPERS_ENTRY)?.tabs ?? [];

  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [timeRemaining, setTimeRemaining] = useState<string>('');
  const [isExpiringSoon, setIsExpiringSoon] = useState(false);
  const lastActivityRef = React.useRef<number>(Date.now());

  useEffect(() => {
    const markActive = () => { lastActivityRef.current = Date.now(); };
    window.addEventListener('click', markActive, { passive: true });
    window.addEventListener('keydown', markActive, { passive: true });
    return () => {
      window.removeEventListener('click', markActive);
      window.removeEventListener('keydown', markActive);
    };
  }, []);

  const formatTime = useCallback((seconds: number): string => {
    if (seconds <= 0) return translate('auth.session_expired');
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    if (h > 0) return `${h}h ${m}m`;
    if (m > 0) return `${m}m ${s}s`;
    return `${s}s`;
  }, [translate]);

  // Check the session every 30 seconds and refresh it while the reader is active.
  useEffect(() => {
    const check = async () => {
      if (!sso.authenticated || !sso.tokenParsed?.exp) {
        setTimeRemaining('');
        return;
      }

      const timeLeft = sso.tokenParsed.exp - Math.floor(Date.now() / 1000);

      if (timeLeft <= 0) {
        clearSsoAuthData();
        window.location.href = `${PORTAL_URL}?app=kyc`;
        return;
      }

      if (timeLeft <= 300) {
        const idleMs = Date.now() - lastActivityRef.current;
        if (idleMs < 5 * 60 * 1000) {
          try {
            const refreshed = await sso.updateToken(60);
            if (refreshed) syncAuthToLocalStorage(sso);
          } catch {
            clearSsoAuthData();
            window.location.href = `${PORTAL_URL}?app=kyc`;
            return;
          }
        }
      }

      setIsExpiringSoon(timeLeft <= 300);
      setTimeRemaining(formatTime(timeLeft));
    };

    check();
    const interval = setInterval(check, 30_000);
    return () => clearInterval(interval);
  }, [formatTime]);

  if (isLoading) return null;

  const userName = identity?.fullName || '—';
  const rawRole = (identity as { role?: string } | undefined)?.role;
  const roleLabel = rawRole ? translate(`roles.${rawRole}`, { _: rawRole }) : '';
  const email = (identity as { email?: string } | undefined)?.email;

  const openMenu = (e: React.SyntheticEvent<HTMLElement>) => setAnchor(e.currentTarget);
  const onKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setAnchor(e.currentTarget);
    }
  };

  return (
    <Box sx={{ mt: 1, pb: '12px' }}>
      <Tooltip title={expanded ? '' : `${userName} · ${roleLabel}`} placement="right">
        <Box
          role="button"
          tabIndex={0}
          aria-haspopup="menu"
          aria-expanded={Boolean(anchor)}
          aria-label={translate('shell.profile.menu')}
          onClick={openMenu}
          onKeyDown={onKeyDown}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: expanded ? 'flex-start' : 'center',
            gap: '10px',
            padding: expanded ? '8px 10px' : 0,
            borderRadius: profile.card.borderRadius,
            cursor: 'pointer',
            backgroundColor: expanded ? profile.card.bg : 'transparent',
            border: `1px solid ${expanded ? profile.card.border : 'transparent'}`,
            transition: 'background-color .15s, border-color .15s',
            '&:hover': expanded ? { backgroundColor: profile.card.hoverBg } : undefined,
            '&:focus-visible': {
              outline: `2px solid ${theme.palette.primary.main}`,
              outlineOffset: '2px',
            },
          }}
        >
          <Avatar
            sx={{
              width: profile.card.avatarSize,
              height: profile.card.avatarSize,
              fontSize: '12px',
              fontWeight: 700,
              backgroundColor: profile.card.avatarBg,
              color: profile.card.avatarColor,
            }}
          >
            {getInitials(identity?.fullName)}
          </Avatar>
          {expanded && (
            <>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                  noWrap
                  sx={{ fontSize: profile.nameFontSize, fontWeight: profile.nameFontWeight, color: profile.card.nameColor }}
                >
                  {userName}
                </Typography>
                <Typography
                  noWrap
                  sx={{ fontSize: profile.roleFontSize, fontWeight: profile.roleFontWeight, color: profile.card.roleColor }}
                >
                  {roleLabel}
                </Typography>
              </Box>
              <UnfoldMoreIcon sx={{ fontSize: '18px', color: profile.card.chevronColor }} />
            </>
          )}
        </Box>
      </Tooltip>

      {/* Opens upwards out of the foot of the rail, as wide as the open rail
          minus its gutter in both states — 64px would not fit a name. */}
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: { ...theme.custom.userMenu.paper, width: `calc(${theme.custom.menu.openWidth} - 20px)` },
          },
        }}
      >
        <Box sx={{ padding: '10px 14px 8px' }}>
          <Typography noWrap sx={{ fontSize: '14px', fontWeight: 600, color: profile.card.nameColor }}>
            {userName}
          </Typography>
          {email && (
            <Typography noWrap sx={{ fontSize: '13px', color: theme.palette.text.secondary }}>
              {email}
            </Typography>
          )}
          <Typography noWrap sx={{ fontSize: profile.roleFontSize, color: profile.card.roleColor }}>
            {roleLabel}
          </Typography>
          {timeRemaining && (
            <Chip
              icon={<TimeIcon />}
              label={translate('shell.profile.session', { time: timeRemaining })}
              size="small"
              color={isExpiringSoon ? 'warning' : 'default'}
              sx={{ mt: 1, '& .MuiChip-icon': { fontSize: 16 } }}
            />
          )}
        </Box>
        <Divider />
        {/* Direct children of the menu, not wrapped: the arrow keys only walk direct children. */}
        {developerTabs.length > 0 && [
          <Typography
            key="developers-heading"
            sx={{ padding: '8px 14px 4px', fontSize: profile.roleFontSize, fontWeight: 600, color: theme.palette.text.secondary }}
          >
            {translate('menu.developers')}
          </Typography>,
          ...developerTabs.map((tab) => (
            <MenuItem
              key={tab.key}
              onClick={() => {
                setAnchor(null);
                navigate(tab.to);
              }}
            >
              <ListItemText>{translate(tab.labelKey)}</ListItemText>
            </MenuItem>
          )),
          <Divider key="developers-divider" />,
        ]}
        {/* Each language is named in its own language: a reader who needs to
            switch cannot be assumed to understand the one on screen. */}
        <Typography
          sx={{ padding: '8px 14px 4px', fontSize: profile.roleFontSize, fontWeight: 600, color: theme.palette.text.secondary }}
        >
          {translate('shell.profile.language')}
        </Typography>
        {locales.map((entry) => {
          const selected = entry.locale === locale;
          return (
            <MenuItem
              key={entry.locale}
              selected={selected}
              aria-current={selected ? 'true' : undefined}
              onClick={() => {
                setAnchor(null);
                if (!selected) setLocale(entry.locale);
              }}
            >
              <ListItemIcon>
                {selected ? (
                  <CheckIcon fontSize="small" sx={{ color: theme.palette.primary.dark }} />
                ) : (
                  <TranslateIcon fontSize="small" />
                )}
              </ListItemIcon>
              <ListItemText primaryTypographyProps={{ fontWeight: selected ? 600 : 400 }}>{entry.name}</ListItemText>
            </MenuItem>
          );
        })}
        <Divider />
        <MenuItem
          onClick={() => {
            setAnchor(null);
            logout();
          }}
        >
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{translate('ra.auth.logout')}</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default ProfileRailCard;
